import { Observable, Subscription } from 'rxjs';
import type { ApiResponse } from '../types';
import type { FamilyPlanMemberView, FamilyPlanView } from '../types/family-plan-view.types';
import type {
  FamilyMovePayload,
  FamilyMovePreview,
  FamilyMoveResult,
} from '../types/family-members.types';
import { familyMoveErrorMessage, isMovePhoneValid, needsMemberName } from './family-move.util';

/** The calls this controller makes; implemented by FamilyMembersService. */
export interface FamilyMoveActions {
  movePreview(
    groupId: string,
    memberId: string,
    phone: string
  ): Observable<ApiResponse<FamilyMovePreview>>;
  moveMember(
    groupId: string,
    memberId: string,
    payload: FamilyMovePayload
  ): Observable<ApiResponse<FamilyMoveResult>>;
}

export interface FamilyMoveOptions {
  /** The plate was moved: reload the family. */
  onMoved?: (member: FamilyPlanMemberView) => void;
}

export type FamilyMoveStep = 'phone' | 'confirm';

/**
 * State behind the "Move to own number" pop-up of the Family Plan tab: the number, whether that
 * number already has a customer, and the request. The plate keeps billing the primary's card, so
 * nothing changes on Stripe.
 */
export class FamilyMoveFlowController {
  member: FamilyPlanMemberView | null = null;
  step: FamilyMoveStep = 'phone';
  phone = '';
  firstName = '';
  lastName = '';
  preview: FamilyMovePreview | null = null;
  busy = false;
  error = '';

  private groupId = '';
  private request = Subscription.EMPTY;

  constructor(
    private readonly actions: FamilyMoveActions,
    private readonly options: FamilyMoveOptions = {}
  ) {}

  get isOpen(): boolean {
    return this.member !== null;
  }

  /** The member's name is asked for when the number has no customer yet. */
  get asksName(): boolean {
    return this.step !== 'phone' && needsMemberName(this.preview);
  }

  get canLookUp(): boolean {
    return !this.busy && isMovePhoneValid(this.phone);
  }

  get canMove(): boolean {
    return (
      !this.busy &&
      this.step === 'confirm' &&
      this.preview !== null &&
      (!this.asksName || this.firstName.trim() !== '')
    );
  }

  open(view: FamilyPlanView | null, member: FamilyPlanMemberView): void {
    this.close();
    this.groupId = view?.group?.id ?? '';
    this.member = member;
  }

  close(): void {
    this.request.unsubscribe();
    this.member = null;
    this.step = 'phone';
    this.phone = '';
    this.firstName = '';
    this.lastName = '';
    this.preview = null;
    this.busy = false;
    this.error = '';
  }

  /** Back to the number, which may be changed: nothing chosen so far is kept. */
  editNumber(): void {
    this.request.unsubscribe();
    this.step = 'phone';
    this.preview = null;
    this.busy = false;
    this.error = '';
  }

  lookUp(): void {
    const member = this.member;
    if (!member || !this.canLookUp) return;
    this.busy = true;
    this.error = '';
    this.request = this.actions.movePreview(this.groupId, member.member_id, this.phone).subscribe({
      next: response => {
        this.busy = false;
        if (!response?.data) {
          this.error = familyMoveErrorMessage(null);
          return;
        }
        this.preview = response.data;
        this.step = 'confirm';
      },
      error: error => {
        this.busy = false;
        this.error = familyMoveErrorMessage(error);
      },
    });
  }

  move(): void {
    const member = this.member;
    if (!member || !this.canMove) return;
    this.busy = true;
    this.error = '';
    this.request = this.actions
      .moveMember(this.groupId, member.member_id, {
        phone: this.phone,
        ...this.names(),
      })
      .subscribe({
        next: () => {
          this.close();
          this.options.onMoved?.(member);
        },
        error: error => {
          this.busy = false;
          this.error = familyMoveErrorMessage(error);
        },
      });
  }

  destroy(): void {
    this.request.unsubscribe();
  }

  /** The names go with the request only when the number has no customer to take them from. */
  private names(): { first_name?: string; last_name?: string } {
    if (!this.asksName) return {};
    return {
      ...(this.firstName.trim() ? { first_name: this.firstName.trim() } : {}),
      ...(this.lastName.trim() ? { last_name: this.lastName.trim() } : {}),
    };
  }
}
